import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserResponse } from '../types/user';
import useUserApi from '../api/useUserApi';

interface AuthContextType {
  user: UserResponse | null;
  isAdmin: boolean;
  isLoading: boolean;
  reloadUser: () => void;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { login: loginApi, getCurrentUser } = useUserApi();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      getCurrentUser()
        .then((userData) => {
          setUser(userData);
        })
        .catch(() => {
          localStorage.removeItem('token');
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [getCurrentUser]);

  const reloadUser = async () => {
    const userData = await getCurrentUser();
    setUser(userData);
  };

  const login = async (username: string, password: string) => {
    const response = await loginApi({ username, password });
    localStorage.setItem('token', response.access_token);
    const userData = await getCurrentUser();
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const isAdmin = user?.is_admin || false;

  return (
    <AuthContext.Provider value={{ user, isAdmin, isLoading, login, logout, reloadUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 